// ═══════════════════════════════════════════════════════════════
// MIGRACIÓN: /usuarios — asignar rolId v2 + completar /asignaciones
// ═══════════════════════════════════════════════════════════════
//
// Complementa B2 (que migró /accesos → /asignaciones para consultores).
// Este script (B3):
//   1. Asigna `rolId` a cada usuario según el mapping vistaSlug → rolId.
//   2. Para usuarios sin entrada en /accesos pero con empresaIds[] (clientes,
//      empleados de empresa, contadores, etc.), genera las asignaciones faltantes.
//   3. Recalcula `empresaIds[]` como CACHE DERIVADO (fuente de verdad: /asignaciones).
//   4. Setea `esWizard` booleano derivado.
//
// MAPPING vistaSlug → rolId:
//   power        → WIZARD            (Mariano y similares)
//   supervisor   → SUPERVISOR
//   consultor    → CONSULTOR
//   coordinador  → SUPERVISOR         (no hay rol Coordinador en v2; se mapea acá)
//   operador     → OPERADOR
//   asesor       → ASESOR
//   (sin vistaSlug, tipo='cliente')       → OWNER_EMPRESA  (default si es dueño)
//   (sin vistaSlug, tipo='colaborador')   → EMPLEADO       (default)
//
// PRINCIPIO: Mariano (WIZARD) tiene que quedar funcional. Verificamos antes y después.
//
// IDS DE ROL: usamos `codigo` del rol como rolId (string en mayúscula: 'WIZARD',
// 'OPERACIONES', etc.). Es el mismo patrón usado en seed-roles-v2.js.
//
// IDS DE ASIGNACIÓN: deterministas `${userId}__${scopeKey}` — re-run no duplica.
//
// Uso desde consola admin:
//   await __migrateUsuariosV2({ dryRun: true })   ← dry-run primero
//   await __migrateUsuariosV2()                    ← ejecutar real
//   await __verificarMigracionUsuariosV2()         ← validar resultado
// ═══════════════════════════════════════════════════════════════

import {
  collection, getDocs, getDoc, setDoc, doc, query, where, serverTimestamp,
} from 'firebase/firestore'
import { db } from '@core/lib/firebase'
import { docConId } from '@core/lib/docConId'
import { COLLECTIONS } from '@core/lib/constants'

const MARIANO_EMAIL = 'mariano@milbrands.com.ar'

// ─────────────────────────────────────────────────────────────────
// MAPPING vistaSlug → rolId
// ─────────────────────────────────────────────────────────────────

const MAPPING_VISTA_A_ROL = {
  power:        'WIZARD',
  supervisor:   'SUPERVISOR',
  consultor:    'CONSULTOR',
  coordinador:  'SUPERVISOR',   // sin rol Coordinador en v2; sube a Supervisor
  operador:     'OPERADOR',
  asesor:       'ASESOR',
}

/**
 * Determina el rolId v2 que corresponde a un usuario legacy.
 * Reglas en orden de prioridad:
 *   1. Si tiene vistaSlug → usar mapping de la tabla.
 *   2. Si tipo='cliente' o 'dueno' → OWNER_EMPRESA por default.
 *   3. Si tipo='colaborador' → EMPLEADO por default. Si vino marcado como
 *      "contador", "asesor", etc. en su perfil, usar ese rol.
 *   4. Si tipo='consultor' o email @milbrands.com.ar → CONSULTOR.
 *   5. Sin nada definido → null (lo reportamos como "raro").
 */
function determinarRolId(usuario) {
  // 1. vistaSlug mapping
  if (usuario.vistaSlug && MAPPING_VISTA_A_ROL[usuario.vistaSlug]) {
    return MAPPING_VISTA_A_ROL[usuario.vistaSlug]
  }
  // 2. Mariano explícito → WIZARD
  if (usuario.email?.toLowerCase() === MARIANO_EMAIL) {
    return 'WIZARD'
  }
  // 3. Por email @milbrands → Consultor (default operativo)
  if (usuario.email?.toLowerCase().endsWith('@milbrands.com.ar')) {
    return 'CONSULTOR'
  }
  // 4. Por tipo
  if (usuario.tipo === 'cliente' || usuario.tipo === 'dueno' || usuario.tipo === 'owner') {
    return 'OWNER_EMPRESA'
  }
  if (usuario.tipo === 'colaborador') {
    return 'EMPLEADO'  // genérico; el admin puede ajustar después
  }
  if (usuario.tipo === 'milbrands' || usuario.tipo === 'consultor') {
    return 'CONSULTOR'
  }
  return null  // raro — reportar
}

// ─────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────

function scopeKey(scope) {
  if (!scope) return 'invalid'
  if (scope.wizard === true) return 'wizard'
  if (scope.sistemaTotal === true) return 'sistema'
  if (scope.tipo === 'empresa' && scope.empresaId) return `empresa_${scope.empresaId}`
  if (scope.tipo === 'grupo' && scope.grupoId) return `grupo_${scope.grupoId}`
  if (scope.tipo === 'supervision') return `supervision_${(scope.consultoresIds || []).slice().sort().join('-')}`
  return `unknown_${scope.tipo || 'x'}`
}

function asignacionIdParaUserScope(userId, scope) {
  return `${userId}__${scopeKey(scope)}`
}

/**
 * Asegura que existe una asignación tipo=empresa para (userId, empresaId).
 * Idempotente. Retorna {creada: boolean, asignacionId}.
 */
async function ensureAsignacionEmpresa(userId, empresaId, opts = {}) {
  const { dryRun = false, creadoPor = null } = opts
  const scope = { tipo: 'empresa', empresaId }
  const asignacionId = asignacionIdParaUserScope(userId, scope)
  const ref = doc(db, COLLECTIONS.ASIGNACIONES, asignacionId)
  const snap = await getDoc(ref)
  if (snap.exists()) {
    return { creada: false, asignacionId, _existia: true }
  }
  if (!dryRun) {
    await setDoc(ref, {
      userId,
      scope,
      desde: serverTimestamp(),
      hasta: null,
      esActivo: true,
      creadoPor: creadoPor || userId,
      creadoEn: serverTimestamp(),
      actualizadoEn: serverTimestamp(),
      schemaVersion: 2,
      _origen: 'migrate-usuarios-v2',
    })
  }
  return { creada: true, asignacionId, _existia: false }
}

/**
 * Recalcula empresaIds[] derivado de las asignaciones del usuario.
 * Cache denormalizado: la fuente de verdad es /asignaciones.
 * Se usa en Firestore Rules para multi-tenant filtering (no se puede hacer
 * el lookup desde rules sin pegar costo prohibitivo).
 */
async function recalcularEmpresaIdsDerivado(userId) {
  const q = query(
    collection(db, COLLECTIONS.ASIGNACIONES),
    where('userId', '==', userId),
    where('esActivo', '==', true),
  )
  const snap = await getDocs(q)
  const empresas = new Set()
  let esWizard = false
  let tieneSistemaTotal = false

  for (const d of snap.docs) {
    const a = d.data()
    if (a.scope?.wizard === true) esWizard = true
    if (a.scope?.sistemaTotal === true) tieneSistemaTotal = true
    if (a.scope?.tipo === 'empresa' && a.scope.empresaId) {
      empresas.add(a.scope.empresaId)
    }
  }
  return {
    empresaIds: Array.from(empresas),
    esWizard,
    tieneSistemaTotal,
  }
}

// ─────────────────────────────────────────────────────────────────
// MIGRACIÓN PRINCIPAL
// ─────────────────────────────────────────────────────────────────

/**
 * Migra todos los usuarios al modelo v2 (rolId + asignaciones derivadas + cache).
 *
 * @param {{dryRun?: boolean, soloUid?: string}} opts
 *    dryRun  — no escribe, solo simula
 *    soloUid — procesa solo este uid (útil para validar primero con Mariano)
 * @returns {Promise<{
 *   total, migrados, raros, asignacionesCreadas, errores
 * }>}
 */
export async function migrateUsuariosV2(opts = {}) {
  const { dryRun = false, soloUid = null } = opts
  console.groupCollapsed(`[migrateUsuariosV2] dryRun=${dryRun} soloUid=${soloUid || 'todos'}`)

  const snap = await getDocs(collection(db, COLLECTIONS.USUARIOS))
  const todos = snap.docs.map(docConId)
  const usuarios = soloUid ? todos.filter(u => u.id === soloUid || u.uid === soloUid) : todos
  console.log(`  ${usuarios.length}/${todos.length} usuarios a procesar`)

  const stats = {
    total: usuarios.length,
    migrados: 0,
    yaTeniaRolId: 0,
    raros: [],            // usuarios sin rolId determinable
    asignacionesCreadas: 0,
    asignacionesYaExistian: 0,
    errores: [],
    detalles: [],
  }

  for (const u of usuarios) {
    try {
      const userId = u.uid || u.id
      const rolId = determinarRolId(u)
      if (!rolId) {
        stats.raros.push({
          id: u.id, email: u.email, tipo: u.tipo, vistaSlug: u.vistaSlug,
        })
        console.warn(`[migrate] RARO (sin rolId determinable):`, u.email || u.id)
        continue
      }

      // 1. Crear asignaciones desde empresaIds[] (si no las hay ya en /asignaciones).
      const empresaIdsLegacy = Array.isArray(u.empresaIds) ? u.empresaIds : []
      const detalleAsigs = []
      for (const eid of empresaIdsLegacy) {
        const r = await ensureAsignacionEmpresa(userId, eid, { dryRun, creadoPor: userId })
        detalleAsigs.push({ empresaId: eid, ...r })
        if (r.creada) stats.asignacionesCreadas++
        else stats.asignacionesYaExistian++
      }

      // 2. Recalcular cache derivado.
      let cache = { empresaIds: empresaIdsLegacy, esWizard: false, tieneSistemaTotal: false }
      if (!dryRun) {
        cache = await recalcularEmpresaIdsDerivado(userId)
      }

      // 3. Actualizar doc del usuario: rolId + cache derivado.
      if (!dryRun) {
        await setDoc(doc(db, COLLECTIONS.USUARIOS, u.id), {
          rolId,
          rolPlataformaId: null,
          empresaIds: cache.empresaIds,     // DERIVADO desde acá en adelante
          esWizard: cache.esWizard,
          schemaVersionUsuario: 2,
          actualizadoEn: serverTimestamp(),
        }, { merge: true })
      }

      stats.migrados++
      stats.detalles.push({
        id: u.id, email: u.email, vistaSlug: u.vistaSlug, rolIdAsignado: rolId,
        empresaIds: cache.empresaIds, esWizard: cache.esWizard,
        asignaciones: detalleAsigs,
      })
      console.log(`  ✓ ${u.email || u.id} → rolId=${rolId}, empresaIds=[${cache.empresaIds.length}], wizard=${cache.esWizard}`)
    } catch (e) {
      console.error(`[migrate] ERROR en ${u.email || u.id}:`, e)
      stats.errores.push({ id: u.id, email: u.email, error: e.message || String(e) })
    }
  }

  console.log(`[migrateUsuariosV2] resumen:`, {
    total: stats.total,
    migrados: stats.migrados,
    raros: stats.raros.length,
    asignacionesCreadas: stats.asignacionesCreadas,
    asignacionesYaExistian: stats.asignacionesYaExistian,
    errores: stats.errores.length,
  })
  if (stats.raros.length > 0) {
    console.warn(`[migrateUsuariosV2] ${stats.raros.length} usuarios SIN rolId (revisar manual):`, stats.raros)
  }
  console.groupEnd()
  return stats
}

// ─────────────────────────────────────────────────────────────────
// VALIDACIÓN POST-MIGRACIÓN
// ─────────────────────────────────────────────────────────────────

/**
 * Verifica que la migración esté completa y consistente.
 *   - Cuenta usuarios con/sin rolId.
 *   - Verifica Mariano = WIZARD con empresaIds derivado.
 *   - Detecta usuarios con vistaSlug pero sin rolId (no migrados todavía).
 */
export async function verificarMigracionUsuariosV2() {
  const snap = await getDocs(collection(db, COLLECTIONS.USUARIOS))
  const usuarios = snap.docs.map(docConId)

  const stats = {
    total: usuarios.length,
    conRolId: 0,
    sinRolId: [],
    wizards: [],
    porRol: {},
    inconsistentes: [],  // tiene vistaSlug pero no rolId
  }

  for (const u of usuarios) {
    if (u.rolId) {
      stats.conRolId++
      stats.porRol[u.rolId] = (stats.porRol[u.rolId] || 0) + 1
      if (u.esWizard === true) stats.wizards.push({ id: u.id, email: u.email })
    } else {
      stats.sinRolId.push({ id: u.id, email: u.email, vistaSlug: u.vistaSlug, tipo: u.tipo })
      if (u.vistaSlug) stats.inconsistentes.push({ id: u.id, email: u.email, vistaSlug: u.vistaSlug })
    }
  }

  console.group('[verificarMigracionUsuariosV2]')
  console.log(`  Total usuarios: ${stats.total}`)
  console.log(`  Con rolId: ${stats.conRolId}`)
  console.log(`  Sin rolId: ${stats.sinRolId.length}`, stats.sinRolId.length > 0 ? stats.sinRolId : '')
  console.log(`  Por rol:`, stats.porRol)
  console.log(`  WIZARDS:`, stats.wizards)
  if (stats.inconsistentes.length > 0) {
    console.warn(`  ⚠️ INCONSISTENTES (vistaSlug pero sin rolId): ${stats.inconsistentes.length}`, stats.inconsistentes)
  }
  console.groupEnd()
  return stats
}

/**
 * Test rápido: ¿Mariano (WIZARD) tiene asignación wizard + esWizard=true + rolId=WIZARD?
 */
export async function verificarMariano() {
  const q = query(
    collection(db, COLLECTIONS.USUARIOS),
    where('email', '==', MARIANO_EMAIL),
  )
  const snap = await getDocs(q)
  if (snap.empty) {
    console.error(`❌ Mariano (${MARIANO_EMAIL}) NO encontrado en /usuarios`)
    return { ok: false, error: 'no encontrado' }
  }
  const docs = snap.docs.map(docConId)
  // Preferir el doc donde uid==id (real Firebase Auth)
  const m = docs.find(d => d.uid === d.id) || docs[0]
  const userId = m.uid || m.id

  // Buscar asignación wizard
  const qa = query(
    collection(db, COLLECTIONS.ASIGNACIONES),
    where('userId', '==', userId),
    where('esActivo', '==', true),
  )
  const sa = await getDocs(qa)
  const asignaciones = sa.docs.map(docConId)
  const wizardAsig = asignaciones.find(a => a.scope?.wizard === true)

  const checks = {
    docId: m.id,
    uid: userId,
    rolIdEsWizard: m.rolId === 'WIZARD',
    esWizardDerivado: m.esWizard === true,
    asignacionWizardExiste: !!wizardAsig,
    asignacionesActivas: asignaciones.length,
    empresaIdsDerivados: Array.isArray(m.empresaIds) ? m.empresaIds.length : 0,
  }

  const ok = checks.rolIdEsWizard && checks.esWizardDerivado && checks.asignacionWizardExiste
  console.group(`[verificarMariano] ok=${ok}`)
  console.log('  checks:', checks)
  console.log('  asignaciones de Mariano:', asignaciones)
  console.groupEnd()
  return { ok, checks, asignaciones }
}

// ─────────────────────────────────────────────────────────────────
// EXPOSICIÓN EN WINDOW
// ─────────────────────────────────────────────────────────────────

if (typeof window !== 'undefined') {
  // eslint-disable-next-line no-undef
  window.__migrateUsuariosV2 = migrateUsuariosV2
  // eslint-disable-next-line no-undef
  window.__verificarMigracionUsuariosV2 = verificarMigracionUsuariosV2
  // eslint-disable-next-line no-undef
  window.__verificarMariano = verificarMariano
}
